// #region: Class definition *********************************************************************/
/**
 * @class PermissionsManager
 * @description Manages browser permissions using the Permissions API as the source of truth.
 * Only stores user preferences (like 'remember choice'), never permission grants/denials.
 * 
 * @example
 * // Check camera permission.
 * const cameraStatus = await permissionsManager.getCameraPermissionStatus();
 * 
 * @example
 * // Request location permission.
 * const locationGranted = await permissionsManager.requestLocationPermission();
 * 
 * @remarks
 * Uses the Permissions API as the authoritative source for permission status.
 * Only stores user preferences in localStorage with expiry for privacy.
 * Permission grants/denials are never persisted as users can revoke them at any time.
 * 
 * @memberof window
 */
window.permissionsManager = new class {

    // #region: Private Fields *******************************************************************/
    #cameraRememberKey;
    #locationRememberKey;
    #preferenceExpire;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of PermissionsManager.
     */
    constructor() {
        this.#cameraRememberKey = 'camera_remember_permission';
        this.#locationRememberKey = 'location_remember_permission';

        // User preferences expire after 30 days to respect privacy.
        this.#preferenceExpire = 30 * 24 * 60 * 60 * 1000;
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     */
    toString() {
        return '[PermissionsManager]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     */
    static toString() {
        return '[PermissionsManager]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Clears all stored preference data.
     * Useful for testing or privacy reset.
     */
    clearAllPreferences() {
        localStorage.removeItem(this.#cameraRememberKey);
        localStorage.removeItem(this.#locationRememberKey);
    }

    /**
     * Gets the current camera permission status from the Permissions API.
     * @returns {Promise<string>} Promise resolving to 'granted', 'denied', 'prompt', or 'unknown'.
     */
    async getCameraPermissionStatus() {
        // Check if camera is supported.
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            return 'unknown';
        }

        // Feature-detect Permissions API before querying.
        if ('permissions' in navigator && typeof navigator.permissions.query === 'function') {
            try {
                const result = await navigator.permissions.query({ name: 'camera' });

                return result.state;
            }
            catch (error) {
                // Safari lacks 'camera' support in Permissions API, return 'prompt' for graceful degradation.
                return 'prompt';
            }
        }

        // No Permissions API support, return 'prompt' for graceful degradation.
        return 'prompt';
    }

    /**
     * Gets the current location permission status from the Permissions API.
     * @returns {Promise<string>} Promise resolving to 'granted', 'denied', 'prompt', or 'unknown'.
     */
    async getLocationPermissionStatus() {
        // Check if geolocation is supported.
        if (!('geolocation' in navigator)) {
            return 'unknown';
        }

        // Feature-detect Permissions API before querying.
        if ('permissions' in navigator && typeof navigator.permissions.query === 'function') {
            try {
                const result = await navigator.permissions.query({ name: 'geolocation' });

                return result.state;
            }
            catch (error) {
                // Permissions API may not support geolocation query in some browsers.
                return 'prompt';
            }
        }

        // No Permissions API support, return 'prompt' for graceful degradation.
        return 'prompt';
    }

    /**
     * Requests camera permission by attempting to access the camera.
     * @param {Object} [constraints] Optional camera constraints.
     * @returns {Promise<boolean>} Promise resolving to true if granted, false otherwise.
     */
    async requestCameraPermission(constraints = {}) {
        // Check current status first.
        const currentStatus = await this.getCameraPermissionStatus();

        if (currentStatus === 'unknown') {
            return false;
        }

        if (currentStatus === 'granted') {
            return true;
        }

        if (currentStatus === 'denied') {
            return false;
        }

        // Need to request permission by attempting camera access.
        try {
            const defaultConstraints = {
                video: {
                    facingMode: 'environment'
                },
                audio: false
            };

            const mergedConstraints = {
                ...defaultConstraints,
                ...constraints
            };

            // Test camera access to trigger permission prompt.
            const stream = await navigator.mediaDevices.getUserMedia(mergedConstraints);

            // Permission granted, clean up test stream.
            stream.getTracks().forEach((track) => {
                track.stop();
            });

            return true;
        }
        catch (error) {
            // Permission denied or other error.
            return false;
        }
    }

    /**
     * Requests location permission by attempting to access geolocation.
     * @returns {Promise<boolean>} Promise resolving to true if granted, false otherwise.
     */
    async requestLocationPermission() {
        // Check current status first.
        const currentStatus = await this.getLocationPermissionStatus();

        if (currentStatus === 'unknown') {
            return false;
        }

        if (currentStatus === 'granted') {
            return true;
        }

        if (currentStatus === 'denied') {
            return false;
        }

        // Need to request permission by attempting location access.
        try {
            await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    resolve,
                    reject,
                    {
                        enableHighAccuracy: false,
                        maximumAge: Infinity,
                        timeout: 10000
                    }
                );
            });

            return true;
        }
        catch (error) {
            return false;
        }
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Sets the remember choice preference for camera.
     * @param {boolean} remember Whether to remember the user's choice.
     */
    setCameraRememberChoice(remember) {
        this.#setRememberPreference(this.#cameraRememberKey, remember);
    }

    /**
     * Gets whether the user chose to remember their camera choice.
     * @returns {boolean} True if user wants to remember their choice.
     */
    shouldRememberCameraChoice() {
        return this.#shouldRememberChoice(this.#cameraRememberKey);
    }

    /**
     * Sets the remember choice preference for location.
     * @param {boolean} remember Whether to remember the user's choice.
     */
    setLocationRememberChoice(remember) {
        this.#setRememberPreference(this.#locationRememberKey, remember);
    }

    /**
     * Gets whether the user chose to remember their location choice.
     * @returns {boolean} True if user wants to remember their choice.
     */
    shouldRememberLocationChoice() {
        return this.#shouldRememberChoice(this.#locationRememberKey);
    }

    /**
     * Gets stored preference data if not expired.
     * @param {string} key The storage key for the preference.
     * @returns {Object|null} Stored preference object or null if not found/expired.
     * 
     * @private
     */
    #getStoredPreference(key) {
        try {
            const stored = localStorage.getItem(key);

            if (!stored) {
                return null;
            }

            const preferenceData = JSON.parse(stored);
            const now = Date.now();

            // Check if preference data has expired.
            if (now - preferenceData.timestamp > this.#preferenceExpire) {
                localStorage.removeItem(key);

                return null;
            }

            return preferenceData;
        }
        catch (error) {
            // Invalid stored data, remove it.
            localStorage.removeItem(key);

            return null;
        }
    }

    /**
     * Sets remember preference with timestamp.
     * @param {string} key The storage key for the preference.
     * @param {boolean} remember Whether to remember the choice.
     * 
     * @private
     */
    #setRememberPreference(key, remember) {
        try {
            const preferenceData = {
                remember: remember,
                timestamp: Date.now()
            };

            localStorage.setItem(key, JSON.stringify(preferenceData));
        }
        catch (error) {
            // Storage failed, continue without storing.
            window.isDebugMode && console.warn('Failed to store preference data:', error);
        }
    }

    /**
     * Checks if user wants to remember their choice.
     * @param {string} key The storage key for the preference.
     * @returns {boolean} True if user wants to remember their choice.
     * 
     * @private
     */
    #shouldRememberChoice(key) {
        const preference = this.#getStoredPreference(key);

        return preference ? preference.remember : false;
    }
    // #endregion ********************************************************************************/

}();
// #endregion ************************************************************************************/